import { useRef, useState } from 'react';
import {
  OnboardingMultiDocument,
  OnboardingStoreType,
  OracleOnboardingMultiDocumentSectionKey
} from '@blisspointmedia/bpm-types/dist/ClientOracleOnboarding';
import { reportError } from '@src/utils/errorReporting';
import { MultiDocumentFormValues, MultiDocumentModalCopy } from '../multi-document-constants';
import {
  keepsUploadAfter,
  LastUpload,
  reusableUpload,
  UploadedFile
} from '../multi-document-utils';
import {
  addMultiDocument,
  updateMultiDocument,
  uploadMultiDocumentFile
} from '../onboarding-multi-document-api';
import { useEditorName } from '../useEditorName';
import { useInvalidateMultiDocuments } from '../useMultiDocuments';
import { MultiDocumentModal } from './MultiDocumentModal';

interface MultiDocumentFlowProps {
  company: string;
  store: OnboardingStoreType;
  sectionKey: OracleOnboardingMultiDocumentSectionKey;
  copy: MultiDocumentModalCopy;
  // Present to edit this document. An edit goes to the document's own store.
  doc?: OnboardingMultiDocument;
  onClose: () => void;
}

export const MultiDocumentFlow = ({
  company,
  store,
  sectionKey,
  copy,
  doc,
  onClose
}: MultiDocumentFlowProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const invalidateDocuments = useInvalidateMultiDocuments();
  const editorName = useEditorName();
  const targetStore = doc?.store ?? store;
  const lastUpload = useRef<LastUpload | null>(null);

  const upload = async (file: File, documentId?: number): Promise<UploadedFile> => {
    const reused = reusableUpload(lastUpload.current, file);
    if (reused) {
      return reused;
    }
    const uploaded = await uploadMultiDocumentFile({
      company,
      store: targetStore,
      sectionKey,
      file,
      documentId
    });
    lastUpload.current = { file, uploaded };
    return uploaded;
  };

  const handleSubmit = async (values: MultiDocumentFormValues) => {
    setIsSubmitting(true);
    try {
      if (values.doc) {
        const edit = {
          company,
          store: targetStore,
          documentId: values.doc.id,
          expectedVersionId: values.doc.versionId,
          note: values.note,
          editorName
        };
        await updateMultiDocument(
          values.file ? { ...edit, ...(await upload(values.file, values.doc.id)) } : edit
        );
      } else {
        await addMultiDocument({
          company,
          store: targetStore,
          sectionKey,
          ...(await upload(values.file)),
          note: values.note,
          editorName
        });
      }
      onClose();
    } catch (e) {
      setIsSubmitting(false);
      if (!keepsUploadAfter(e)) {
        lastUpload.current = null;
      }
      reportError(e, { operation: 'OracleOnboarding.saveMultiDocument' });
      throw e;
    } finally {
      // Refetches on failure too; a 409 leaves the cached list stale.
      invalidateDocuments(company, targetStore, sectionKey);
    }
  };

  return (
    <MultiDocumentModal
      sectionKey={sectionKey}
      copy={copy}
      doc={doc}
      onHide={onClose}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
    />
  );
};
